import { Button, Kind } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import { alert } from 'actions/flash'
import {
  Handoff,
  RevenueCenter,
  RevenueCenterConfigRow as ConfigRow,
  useDeleteRevenueCenterConfigMutation,
  useUpsertRevenueCenterConfigMutation,
} from 'api/revenueCenters'
import Badge from 'components/Badge'
import { buildSyncRowLabels } from 'components/buildSyncRowLabels'
import Section from 'components/Section'
import SyncRow from 'components/SyncRow'
import useDispatch from 'hooks/useDispatch'
import { buildTranslate } from 'locales'
import React, { useState } from 'react'
import { merchantTheme } from 'theme'
import RevenueCenterPicker from './RevenueCenterPicker'

const t = buildTranslate('cms.settings.revenue_centers')

const HANDOFFS: Handoff[] = [
  'PICKUP',
  'DELIVERY',
  'CURBSIDE',
  'TABLESIDE',
  'DRIVETHRU',
]

const HANDOFF_ICONS: Record<Handoff, string> = {
  PICKUP: 'fa-shopping-bag',
  DELIVERY: 'fa-motorcycle',
  CURBSIDE: 'fa-car',
  TABLESIDE: 'fa-cutlery',
  DRIVETHRU: 'fa-car',
}

const STATUS_COL_WIDTH = '140px'
const RC_COL_WIDTH = '320px'

type HandoffStatus = 'enabled' | 'default' | 'not_enabled'

type Props = {
  revenueCenters: RevenueCenter[]
  configs: ConfigRow[]
  defaultName: string | undefined
  onSyncNow: () => void
  providerName: string
  syncedAt?: string | null
  isSyncing?: boolean
  supportedHandoffs: Handoff[]
}

const HandoffModesTab = ({
  revenueCenters,
  configs,
  defaultName,
  onSyncNow,
  providerName,
  syncedAt,
  isSyncing = false,
  supportedHandoffs,
}: Props) => {
  const dispatch = useDispatch()
  const [css] = useStyletron()
  const [isEditing, setIsEditing] = useState(false)
  const [drafts, setDrafts] = useState<Partial<Record<Handoff, string>>>({})
  const [isSaving, setIsSaving] = useState(false)
  const [upsert] = useUpsertRevenueCenterConfigMutation()
  const [remove] = useDeleteRevenueCenterConfigMutation()

  const statusCol = css({ width: STATUS_COL_WIDTH, flexShrink: 0 })
  const rcCol = css({ width: RC_COL_WIDTH, flexShrink: 0 })
  const enabledBadge = css({
    backgroundColor: merchantTheme.colors.nephritis10,
    color: merchantTheme.colors.nephritis50,
  })
  const defaultBadge = css({
    backgroundColor: merchantTheme.colors.belizeHole10,
    color: merchantTheme.colors.belizeHole50,
  })
  const notEnabledBadge = css({
    backgroundColor: merchantTheme.colors.grey20,
    color: merchantTheme.colors.grey70,
  })
  const STATUS_BADGE: Record<HandoffStatus, string> = {
    enabled: enabledBadge,
    default: defaultBadge,
    not_enabled: notEnabledBadge,
  }
  const STATUS_LABEL: Record<HandoffStatus, string> = {
    enabled: t('handoff_tab.status_enabled'),
    default: t('handoff_tab.status_default'),
    not_enabled: t('handoff_tab.status_not_enabled'),
  }

  const syncLabels = buildSyncRowLabels(providerName, syncedAt)

  const overrideFor = (handoff: Handoff): string | undefined =>
    configs.find(c => c.handoff === handoff)?.revenue_center_name

  const isHandoffSupported = (handoff: Handoff): boolean =>
    supportedHandoffs.includes(handoff)

  const handoffStatus = (handoff: Handoff): HandoffStatus => {
    if (overrideFor(handoff)) return 'enabled'
    if (defaultName) return 'default'
    return 'not_enabled'
  }

  const startEditing = () => {
    setDrafts({})
    setIsEditing(true)
  }

  const cancelEditing = () => {
    setDrafts({})
    setIsEditing(false)
  }

  const onSave = async () => {
    const changed = HANDOFFS.filter(
      h => h in drafts && drafts[h] !== overrideFor(h)
    )
    if (changed.length === 0) {
      setDrafts({})
      setIsEditing(false)
      return
    }

    setIsSaving(true)
    const results = await Promise.allSettled(
      changed.map(h => {
        const name = drafts[h]
        return name
          ? upsert({ handoff: h, revenue_center_name: name }).unwrap()
          : remove({ handoff: h }).unwrap()
      })
    )
    setIsSaving(false)

    const failed = changed.filter((_, i) => results[i].status === 'rejected')
    if (failed.length > 0) {
      // The writes that succeeded are already persisted, so drop them from the
      // drafts: the table then shows their saved values (via the refetch the
      // mutations trigger) and only the failed handoffs stay editable to retry,
      // rather than re-sending the writes that already took.
      setDrafts(prev => {
        const next = { ...prev }
        changed.forEach((h, i) => {
          if (results[i].status === 'fulfilled') delete next[h]
        })
        return next
      })
      const names = failed.map(h => t(`handoffs.${h.toLowerCase()}`)).join(', ')
      const someSucceeded = failed.length < changed.length
      const messageKey = someSucceeded
        ? 'save_error_handoffs_partial'
        : 'save_error_handoffs'
      dispatch(alert({ key: 'danger', message: t(messageKey, { names }) }))
      return
    }
    dispatch(alert({ key: 'success', message: t('save_success') }))
    setDrafts({})
    setIsEditing(false)
  }

  const renderValueCell = (handoff: Handoff) => {
    const override = overrideFor(handoff)
    const draft = handoff in drafts ? drafts[handoff] : override
    const key = handoff.toLowerCase()

    if (!isHandoffSupported(handoff)) {
      return (
        <RevenueCenterPicker
          revenueCenters={[]}
          value={undefined}
          onChange={() => {}}
          disabled
          placeholder={t('handoff_tab.not_supported')}
          ariaLabel={t(`handoffs.${key}`)}
        />
      )
    }

    if (isEditing) {
      return (
        <RevenueCenterPicker
          revenueCenters={revenueCenters}
          value={draft}
          onChange={name => setDrafts(prev => ({ ...prev, [handoff]: name }))}
          ariaLabel={t(`handoffs.${key}`)}
          placeholder={defaultName ? t('handoff_tab.placeholder') : undefined}
        />
      )
    }
    if (override) {
      return <Text.BodyText4 color="grey90">{override}</Text.BodyText4>
    }
    if (defaultName) {
      return (
        <Text.BodyText4 color="grey50" tag="div">
          {defaultName}
          <Text.BodyText5 color="grey50" tag="div">
            {t('handoff_tab.inherited')}
          </Text.BodyText5>
        </Text.BodyText4>
      )
    }
    return (
      <Text.BodyText4 color="grey50">{t('row.no_selection')}</Text.BodyText4>
    )
  }

  const renderHandoffRow = (handoff: Handoff, muted: boolean) => {
    const key = handoff.toLowerCase()
    const status = muted ? 'not_enabled' : handoffStatus(handoff)

    return (
      <div
        key={handoff}
        role="row"
        className="d-flex align-items-center py-m border-bottom-1 grey-10-border"
        data-testid={`handoff-row-${handoff}`}
      >
        <div role="cell" className="flex-grow-1 d-flex align-items-center">
          <i
            className={`fa ${HANDOFF_ICONS[handoff]} grey-50-text mr-s`}
            aria-hidden="true"
          />
          <div>
            <Text.BodyText3 bold color={muted ? 'grey50' : 'grey90'} tag="div">
              {t(`handoffs.${key}`)}
            </Text.BodyText3>
            <Text.BodyText5 color="grey50" tag="div">
              {t(`handoff_descriptions.${key}`)}
            </Text.BodyText5>
          </div>
        </div>
        <div role="cell" className={`${statusCol} mr-l`}>
          <Badge
            value={STATUS_LABEL[status]}
            className={STATUS_BADGE[status]}
          />
        </div>
        <div role="cell" className={rcCol}>
          {renderValueCell(handoff)}
        </div>
      </div>
    )
  }

  const unsupportedHandoffs = HANDOFFS.filter(h => !isHandoffSupported(h))

  return (
    <>
      <SyncRow
        onSyncNow={onSyncNow}
        syncedAt={syncedAt}
        isSyncing={isSyncing}
        labels={syncLabels}
      />
      <Text.BodyText4 color="grey70" tag="p" className="mb-m">
        {t('handoff_tab.intro')}
      </Text.BodyText4>

      <Section className="p-l">
        <div className="d-flex justify-content-between align-items-start mb-m">
          <Text.Header4 className="m-0">
            {t('handoff_tab.section_title')}
          </Text.Header4>
          {isEditing ? (
            <div className="d-flex">
              <Button
                kind={Kind.PRIMARY}
                onClick={onSave}
                isLoading={isSaving}
                className="mr-s"
              >
                {t('row.save')}
              </Button>
              <Button
                kind={Kind.MINIMAL}
                onClick={cancelEditing}
                disabled={isSaving}
              >
                {t('row.cancel')}
              </Button>
            </div>
          ) : (
            <Button kind={Kind.SECONDARY} onClick={startEditing}>
              {t('row.edit')}
            </Button>
          )}
        </div>

        <div role="table" aria-label={t('handoff_tab.section_title')}>
          <div
            role="row"
            className="d-flex pb-xs border-bottom-1 grey-20-border"
          >
            <div role="columnheader" className="flex-grow-1">
              <Text.SmallCaps3 color="grey50">
                {t('handoff_tab.columns.handoff')}
              </Text.SmallCaps3>
            </div>
            <div role="columnheader" className={`${statusCol} mr-l`}>
              <Text.SmallCaps3 color="grey50">
                {t('handoff_tab.columns.status')}
              </Text.SmallCaps3>
            </div>
            <div role="columnheader" className={rcCol}>
              <Text.SmallCaps3 color="grey50">
                {t('handoff_tab.columns.revenue_center')}
              </Text.SmallCaps3>
            </div>
          </div>

          {HANDOFFS.filter(isHandoffSupported).map(handoff =>
            renderHandoffRow(handoff, false)
          )}

          {unsupportedHandoffs.length > 0 && (
            <div className="py-xs">
              <Text.SmallCaps3 color="grey50">
                {t('handoff_tab.not_supported')}
              </Text.SmallCaps3>
            </div>
          )}

          {unsupportedHandoffs.map(handoff => renderHandoffRow(handoff, true))}
        </div>
      </Section>
    </>
  )
}

export default HandoffModesTab
